import { userQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { ROLE_HIERARCHY } from '@/lib/roles';
import { useAppStore } from '@/stores/app';
import type { PaginatedUsersResponse, UserPublic } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/** Infer primary org as first key in allowed_roles.
 * TODO: use primary_org_id from API when available */
export function inferPrimaryOrgId(user: UserPublic): string | undefined {
  return Object.keys(user.allowed_roles ?? {})[0];
}

/** Infer primary role as highest in ROLE_HIERARCHY within the primary org.
 * TODO: use primary_role from API when available */
export function inferPrimaryRole(user: UserPublic, orgId: string): string | undefined {
  const roles = user.allowed_roles?.[orgId] ?? [];
  return ROLE_HIERARCHY.find(r => roles.includes(r));
}

const ORG_HINT_KEY = (userId: string) => `user-org-hint:${userId}`;
const EMAIL_HINT_KEY = (userId: string) => `user-email-hint:${userId}`;

/** Persist org + email hints so hard-refresh works even for SA-only users
 * (who have no allowed_roles and therefore no org-scoped fallback path).
 * SessionStorage survives F5 but is cleared on tab close. */
function storeUserHints(user: UserPublic): void {
  try {
    const orgId = inferPrimaryOrgId(user);
    if (orgId) sessionStorage.setItem(ORG_HINT_KEY(user.id), orgId);
    if (user.email) sessionStorage.setItem(EMAIL_HINT_KEY(user.id), user.email);
  } catch {
    // storage full or private mode — ignore
  }
}

function readHints(userId: string): { orgId: string | undefined; email: string | undefined } {
  try {
    return {
      orgId: sessionStorage.getItem(ORG_HINT_KEY(userId)) ?? undefined,
      email: sessionStorage.getItem(EMAIL_HINT_KEY(userId)) ?? undefined,
    };
  } catch {
    return { orgId: undefined, email: undefined };
  }
}

/** Find a UserPublic in any cached query that stores a UserPublic[] or PaginatedUsersResponse.
 * Handles both the orgUsers cache (UserPublic[]) and the AG Grid SA datasource cache
 * (PaginatedUsersResponse: { records: UserPublic[] }). */
function findUserInCache(queryClient: ReturnType<typeof useQueryClient>, userId: string): UserPublic | undefined {
  const allEntries = queryClient.getQueriesData<unknown>({
    predicate: query => {
      const data = query.state.data;
      if (!data || typeof data !== 'object') return false;
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        return typeof first === 'object' && first !== null && 'id' in first && 'email' in first;
      }
      if ('records' in data && Array.isArray((data as Record<string, unknown>).records)) {
        return true;
      }
      return false;
    },
  });
  for (const [, data] of allEntries) {
    if (!data) continue;
    if (Array.isArray(data)) {
      const found = (data as UserPublic[]).find(u => u.id === userId);
      if (found) return found;
    }
    if (typeof data === 'object' && 'records' in data) {
      const found = (data as { records: UserPublic[] }).records.find(u => u.id === userId);
      if (found) return found;
    }
  }
  return undefined;
}

export function useUserDetail(userId: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore(state => state.selectedOrganization?.id ?? state.selectedOrgStub?.id ?? '');

  return useQuery<UserPublic | null>({
    // orgId is included so the query re-runs when Zustand's persist middleware
    // hydrates selectedOrgStub from localStorage (async on first render).
    // Without it, the first render runs with orgId='' → caches undefined as fresh
    // → hard-refresh URLs stay stuck on "User not found" for 5 minutes.
    queryKey: [...userQueryKeys.detail(userId), orgId],
    // TODO: replace with apiClient.get<UserPublic>(userQueryKeys.endpoints.detail(userId))
    // when GET /users/{userId} endpoint lands.
    queryFn: async (): Promise<UserPublic | null> => {
      // Fast path: user is already in any cached list (SPA navigation from list view)
      const fromCache = findUserInCache(queryClient, userId);
      if (fromCache) {
        storeUserHints(fromCache);
        return fromCache;
      }

      // Determine which org to search. Prefer the sessionStorage hint — this is
      // the correct org even for SA users whose selected org differs from the
      // user's primary org.
      const { orgId: hintOrgId, email: emailHint } = readHints(userId);
      const effectiveOrgId = hintOrgId || orgId;

      if (effectiveOrgId) {
        try {
          const users = await apiClient.get<UserPublic[]>(`/orgs/${effectiveOrgId}/users`);
          const found = (users ?? []).find(u => u.id === userId);
          if (found) {
            storeUserHints(found);
            return found;
          }
        } catch {
          // fall through to email search
        }
      }

      // SA-only users (no allowed_roles) are not in any org's user list.
      // Fall back to POST /users/search by email — requires SA access, which
      // is only reachable if the viewer is also SA (they reached this page).
      // Will be superseded by GET /users/{userId} once that endpoint lands.
      if (emailHint) {
        try {
          const result = await apiClient.post<PaginatedUsersResponse>(userQueryKeys.endpoints.globalSearch(), {
            skip: 0,
            limit: 1,
            sort_by: 'created_at',
            sort_order: 'desc',
            filters: {
              email: {
                conditions: [{ operator: 'equals', value: emailHint }],
                logic: 'and',
              },
            },
          });
          const found = (result?.records ?? []).find(u => u.id === userId);
          if (found) storeUserHints(found);
          return found ?? null;
        } catch {
          return null;
        }
      }

      return null;
    },
    staleTime: 5 * 60 * 1000,
    initialData: () => {
      const fromOrgCache = queryClient
        .getQueryData<UserPublic[]>(userQueryKeys.orgUsers(orgId))
        ?.find(u => u.id === userId);
      if (fromOrgCache) {
        storeUserHints(fromOrgCache);
        return fromOrgCache;
      }
      // SA fallback: scan broader cache (SA uses server-side datasource, not orgUsers)
      const fromBroaderCache = findUserInCache(queryClient, userId);
      if (fromBroaderCache) storeUserHints(fromBroaderCache);
      return fromBroaderCache;
    },
    initialDataUpdatedAt: () => {
      // Return 0 (epoch) when no orgUsers cache exists so that undefined initialData
      // is treated as stale and queryFn runs immediately.
      return queryClient.getQueryState(userQueryKeys.orgUsers(orgId))?.dataUpdatedAt ?? 0;
    },
  });
}
